// Visual blocks used by the feature posts: each one mirrors something real in the app.
import { Flag, Star, Play, ShieldCheck, Share, PlusSquare, Info } from "lucide-react";
import { FitNotation } from "./Fit";

export const PURPLE = "#8a2be2";
export const BIG_NOTATION = {
  wrap: { minSpacing: 1.6, maxSpacing: 2.6, preferredMeasuresPerLine: 4 },
};

export const skin = (dark) => ({
  panel: dark ? "#241f1c" : "#ffffff",
  border: dark ? "#4b4238" : "#e5e0d8",
  muted: dark ? "#c9c1b5" : "#6e655b",
  ink: dark ? "#fdfbf7" : "#2d2824",
});

const Panel = ({ dark, children, style }) => (
  <div
    style={{
      background: skin(dark).panel,
      border: `3px solid ${skin(dark).border}`,
      borderRadius: 26,
      padding: 24,
      ...style,
    }}
  >
    {children}
  </div>
);

const Label = ({ children }) => (
  <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: 2, color: PURPLE, textTransform: "uppercase" }}>
    {children}
  </div>
);

// ---------------------------------------------------------------- notation
export const NotationPanel = ({ abc, dark, flex = 1, fit = {} }) => (
  <div
    style={{
      background: "#fff",
      border: `3px solid ${skin(dark).border}`,
      borderRadius: 26,
      padding: 22,
      flex,
      minHeight: 0,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <FitNotation abc={abc} {...fit} />
  </div>
);

// --------------------------------------------------- export (fit, 1 page)
export const ExportMock = ({ abc, dark, landscape, fit = {} }) => (
  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 16 }}>
    <Label>{landscape ? "Landscape A4" : "Portrait A4"}{fit.pages > 1 ? ` · page ${(fit.page || 0) + 1}/${fit.pages}` : " · fit to 1 page"}</Label>
    <div
      style={{
        alignSelf: "center",
        width: landscape ? "100%" : "72%",
        aspectRatio: landscape ? "1.414 / 1" : "1 / 1.414",
        background: "#fff",
        border: `3px solid ${skin(dark).border}`,
        borderRadius: 10,
        boxShadow: "0 20px 44px rgba(45,40,36,0.22)",
        padding: 26,
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <FitNotation abc={abc} {...fit} />
      </div>
      <span
        style={{
          position: "absolute",
          right: 20,
          bottom: 16,
          background: "#2d2824",
          color: "#fdfbf7",
          borderRadius: 999,
          padding: "8px 18px",
          fontSize: 21,
          fontWeight: 900,
        }}
      >
        {fit.pages > 1 ? `PAGE ${(fit.page || 0) + 1}/${fit.pages}` : "FIT · 1 page"}
      </span>
    </div>
  </div>
);

// ------------------------------------------------- ABC help chart preview
const CHART = [
  ["Pitch & octaves", ["C D E", "c d e", "c'", "C,"]],
  ["Note length", ["A2", "A/", "A>B"]],
  ["Accidentals", ["^F", "_B", "=F"]],
  ["Bars & repeats", ["|", "|:", ":|", "|1", ":|2"]],
  ["Phrasing", ["(A B)", "A-A", "(3ABC", "[CEG]", "{ag}f"]],
  ["Ornaments", ["~G", "!trill!A", ".A", "u v"]],
  ["Headers & text", ["K:D", "M:6/8", '"Am"', "%"]],
];

export const AbcChartMock = ({ dark }) => (
  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 12 }}>
    {CHART.map(([title, codes]) => (
      <div key={title} style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 300, fontSize: 25, fontWeight: 900, color: skin(dark).ink }}>{title}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {codes.map((c) => (
            <code
              key={c}
              style={{
                background: "#2d2824",
                color: "#fdfbf7",
                borderRadius: 10,
                padding: "8px 14px",
                fontSize: 24,
                fontWeight: 800,
                fontFamily: "ui-monospace, Menlo, monospace",
              }}
            >
              {c}
            </code>
          ))}
        </div>
      </div>
    ))}
    <div style={{ fontSize: 25, fontWeight: 800, color: skin(dark).muted, marginTop: 4 }}>
      Tap the ⓘ in the ABC editor for the full chart
    </div>
  </div>
);

// ------------------------------------------------------- fame search + ranking
const Slider = ({ label, value, dark }) => (
  <div style={{ marginBottom: 26 }}>
    <div style={{ fontSize: 25, fontWeight: 900, color: skin(dark).muted }}>{label}</div>
    <div style={{ marginTop: 12, height: 22, borderRadius: 999, background: skin(dark).border, position: "relative" }}>
      <div style={{ width: `${value}%`, height: "100%", borderRadius: 999, background: PURPLE }} />
      <div
        style={{
          position: "absolute",
          left: `calc(${value}% - 22px)`,
          top: -11,
          width: 44,
          height: 44,
          borderRadius: "50%",
          background: "#fff",
          border: `6px solid ${PURPLE}`,
        }}
      />
    </div>
  </div>
);

export const FameMock = ({ dark }) => (
  <div style={{ flex: 1, minHeight: 0 }}>
    <Slider label="Rare ← → Famous · 86" value={86} dark={dark} />
    <Slider label="Rarely played ← → Often played · 72" value={72} dark={dark} />
    <Panel dark={dark} style={{ marginTop: 4 }}>
      <Label>Why this ranking</Label>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        {[
          ["Settings", "12"],
          ["Recordings", "38"],
          ["Tunebooks", "1,241"],
          ["In sets", "27"],
          ["Base score", "84 / 100"],
          ["Community", "+2"],
        ].map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 26, fontWeight: 800, color: skin(dark).ink }}>
            <span style={{ color: skin(dark).muted }}>{k}</span>
            <span>{v}</span>
          </div>
        ))}
      </div>
    </Panel>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
      {["Spot on", "More famous", "Rarer than this"].map((t, i) => (
        <span
          key={t}
          style={{
            border: `4px solid ${i === 0 ? "#0f8a5f" : PURPLE}`,
            color: i === 0 ? "#0f8a5f" : PURPLE,
            borderRadius: 999,
            padding: "12px 22px",
            fontSize: 25,
            fontWeight: 900,
          }}
        >
          {t}
        </span>
      ))}
    </div>
    <div style={{ fontSize: 25, fontWeight: 800, color: skin(dark).muted, marginTop: 12 }}>
      Signed-in players can contest a ranking — the community moves it by up to ±10 points
    </div>
  </div>
);

// -------------------------------------------------------- name / type / key search
export const NameSearchMock = ({ dark }) => (
  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 16 }}>
    <Panel dark={dark}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ fontSize: 34 }}>🔍</span>
        <span style={{ fontSize: 30, fontWeight: 800, color: skin(dark).ink }}>cooley</span>
      </div>
    </Panel>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
      {["Any type", "reel", "jig", "slip jig", "polka", "waltz"].map((t, i) => (
        <span
          key={t}
          style={{
            border: `4px solid ${i === 1 ? "#FF3366" : skin(dark).border}`,
            background: i === 1 ? "#FF3366" : "transparent",
            color: i === 1 ? "#fff" : skin(dark).ink,
            borderRadius: 999,
            padding: "10px 20px",
            fontSize: 24,
            fontWeight: 900,
          }}
        >
          {t}
        </span>
      ))}
      {["Any key", "Dmaj", "Gmaj", "Amin", "Edor"].map((t, i) => (
        <span
          key={t}
          style={{
            border: `4px solid ${i === 3 ? PURPLE : skin(dark).border}`,
            background: i === 3 ? PURPLE : "transparent",
            color: i === 3 ? "#fff" : skin(dark).ink,
            borderRadius: 999,
            padding: "10px 20px",
            fontSize: 24,
            fontWeight: 900,
          }}
        >
          {t}
        </span>
      ))}
    </div>
    {[
      ["Cooley's", "reel · Edor · 23 settings · 94 recordings"],
      ["Cooley's Dream", "reel · Dmaj · 4 settings"],
      ["Joe Cooley's Reel", "reel · Gmaj · 7 settings"],
    ].map(([n, d]) => (
      <Panel key={n} dark={dark} style={{ padding: 18 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 40, fontWeight: 700, color: skin(dark).ink }}>
          {n}
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color: skin(dark).muted, marginTop: 4 }}>{d}</div>
      </Panel>
    ))}
    <div style={{ fontSize: 25, fontWeight: 800, color: skin(dark).muted }}>
      Partial names and alternative titles work too — every alias in the corpus is searched
    </div>
  </div>
);

// ----------------------------------------------------------------- flags
export const FlagsMock = ({ dark }) => (
  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 14 }}>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
      {[
        ["To learn", "#e11d48"],
        ["Almost there", "#ff6b00"],
        ["In my repertoire", "#0f8a5f"],
        ["For later", "#2563eb"],
      ].map(([label, color]) => (
        <span
          key={label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            border: `4px solid ${color}`,
            background: `${color}1a`,
            color,
            borderRadius: 999,
            padding: "12px 22px",
            fontSize: 26,
            fontWeight: 900,
          }}
        >
          <Flag size={24} fill={color} /> {label}
        </span>
      ))}
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          border: "4px solid #4CAF50",
          background: "#4CAF501a",
          color: "#4CAF50",
          borderRadius: 999,
          padding: "12px 22px",
          fontSize: 26,
          fontWeight: 900,
        }}
      >
        <Star size={24} fill="#4CAF50" /> Favourite
      </span>
    </div>

    <Panel dark={dark} style={{ padding: 18 }}>
      <Label>Premium · unlimited colours · tap “+ New flag”</Label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
        {[5, 6, 7, 8, 9, 10, 11, 12].map((n) => {
          const h = (n * 137.508) % 360;
          const c = `hsl(${h}deg 68% 45%)`;
          return (
            <span
              key={n}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: `4px solid ${c}`,
                color: c,
                borderRadius: 999,
                padding: "10px 18px",
                fontSize: 23,
                fontWeight: 900,
              }}
            >
              <Flag size={20} fill={c} /> Colour {n}
            </span>
          );
        })}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: skin(dark).muted, marginTop: 12 }}>
        Free members keep the four colours above · premium members create as many as they want
      </div>
    </Panel>

    <div>
      <Label>Filter by tune type too</Label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10 }}>
        {[
          ["All types · 46", "#2d2824"],
          ["reels · 21", "#FF3366"],
          ["jigs · 14", "#FFB800"],
          ["polkas · 6", "#00B8A9"],
          ["waltzes · 5", "#8a2be2"],
        ].map(([t, c]) => (
          <span
            key={t}
            style={{
              background: c,
              color: c === "#FFB800" ? "#2d2824" : "#fff",
              borderRadius: 999,
              padding: "10px 20px",
              fontSize: 24,
              fontWeight: 900,
            }}
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  </div>
);

// -------------------------------------------------------------- pricing table
export const PricingTableMock = ({ dark }) => (
  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 14 }}>
    {[
      ["Guest", "free", "5 searches a day · no AI"],
      ["Signed in", "free", "20 searches a day · 3 welcome analyses"],
      ["Premium monthly", "€2 / month", "unlimited searches · 10 analyses a month"],
      ["Premium annual", "€20 / year", "unlimited searches · 15 analyses a month"],
    ].map(([t, p, d]) => (
      <Panel key={t} dark={dark} style={{ padding: 20, display: "flex", alignItems: "center", gap: 18 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 30, fontWeight: 900, color: skin(dark).ink }}>{t}</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: skin(dark).muted, marginTop: 4 }}>{d}</div>
        </div>
        <div style={{ fontSize: 34, fontWeight: 900, color: PURPLE, whiteSpace: "nowrap" }}>{p}</div>
      </Panel>
    ))}
    <Label>Add-on packs · one-off, never expire</Label>
    <div style={{ display: "flex", gap: 14 }}>
      {[
        ["AI pack · 50", "€5", "50 analyses + 50 searches"],
        ["AI pack · 120", "€10", "120 analyses + 120 searches"],
      ].map(([t, p, d]) => (
        <Panel key={t} dark={dark} style={{ padding: 18, flex: 1 }}>
          <div style={{ fontSize: 27, fontWeight: 900, color: skin(dark).ink }}>{t}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: skin(dark).muted, marginTop: 4 }}>{d}</div>
          <div style={{ fontSize: 34, fontWeight: 900, color: PURPLE, marginTop: 8 }}>{p}</div>
        </Panel>
      ))}
    </div>
  </div>
);

// ------------------------------------------------------------------ install
export const InstallMock = ({ dark }) => (
  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 16 }}>
    {[
      ["iPhone & iPad", Share, ["Open foinn.org in Safari", "Tap the Share button", "Add to Home Screen"]],
      ["Android", PlusSquare, ["Open foinn.org in Chrome", "Tap the ⋮ menu", "Install app / Add to Home screen"]],
    ].map(([title, Icon, steps]) => (
      <Panel key={title} dark={dark}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Icon size={34} color={PURPLE} />
          <span style={{ fontSize: 32, fontWeight: 900, color: skin(dark).ink }}>{title}</span>
        </div>
        {steps.map((s, i) => (
          <div key={s} style={{ display: "flex", gap: 14, marginTop: 12, alignItems: "center" }}>
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: PURPLE,
                color: "#fff",
                fontWeight: 900,
                fontSize: 23,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {i + 1}
            </span>
            <span style={{ fontSize: 27, fontWeight: 700, color: skin(dark).ink }}>{s}</span>
          </div>
        ))}
      </Panel>
    ))}
    <div style={{ fontSize: 25, fontWeight: 800, color: skin(dark).muted }}>
      No app store, no account needed — it opens full screen like a real app
    </div>
  </div>
);

// ------------------------------------------------------------------- player
export const PlayerMock = ({ abc, dark, fit = {} }) => (
  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 14 }}>
    <NotationPanel abc={abc} dark={dark} flex={1} fit={fit} />
    <Panel dark={dark} style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <span
          style={{
            width: 76,
            height: 76,
            borderRadius: "50%",
            background: PURPLE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Play size={38} color="#fff" fill="#fff" />
        </span>
        <div style={{ flex: 1 }}>
          <Slider label="Tempo · 92 bpm" value={58} dark={dark} />
          <div style={{ marginTop: -14 }}>
            <Slider label="Zoom · 120%" value={42} dark={dark} />
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 4 }}>
        {["Loop", "Metronome", "Fullscreen", "Follow the notes"].map((b) => (
          <span
            key={b}
            style={{
              border: `4px solid ${skin(dark).border}`,
              color: skin(dark).ink,
              borderRadius: 999,
              padding: "10px 20px",
              fontSize: 24,
              fontWeight: 900,
            }}
          >
            {b}
          </span>
        ))}
      </div>
    </Panel>
  </div>
);

// ---------------------------------------------------------------------- faq
export const FaqMock = ({ dark, items }) => (
  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 14 }}>
    {items.map(([q, a]) => (
      <Panel key={q} dark={dark} style={{ padding: 20 }}>
        <div style={{ display: "flex", gap: 12 }}>
          <Info size={30} color={PURPLE} style={{ flexShrink: 0, marginTop: 4 }} />
          <div>
            <div style={{ fontSize: 29, fontWeight: 900, color: skin(dark).ink }}>{q}</div>
            <div style={{ fontSize: 25, fontWeight: 700, color: skin(dark).muted, marginTop: 6, lineHeight: 1.35 }}>
              {a}
            </div>
          </div>
        </div>
      </Panel>
    ))}
  </div>
);

// ------------------------------------------------------------------ rarity
export const RarityMock = ({ dark }) => (
  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 16 }}>
    <div
      style={{
        border: "5px solid #0f8a5f",
        background: "#0f8a5f14",
        borderRadius: 28,
        padding: 28,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <ShieldCheck size={42} color="#0f8a5f" />
        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 50, fontWeight: 700, color: "#0f8a5f" }}>
          Confirmed rare
        </span>
      </div>
      <div style={{ fontSize: 27, fontWeight: 700, marginTop: 12, lineHeight: 1.35, color: skin(dark).ink }}>
        No more famous tune with this name was found. The rarity looks genuine.
      </div>
    </div>
    <Panel dark={dark} style={{ padding: 20 }}>
      <Label>What it checks</Label>
      {[
        "Every alias and alternative title in the 23,000-tune corpus",
        "Whether a session standard is hiding behind an unfamiliar name",
      ].map((t, i) => (
        <div key={t} style={{ display: "flex", gap: 14, marginTop: 12, alignItems: "flex-start" }}>
          <span
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: PURPLE,
              color: "#fff",
              fontWeight: 900,
              fontSize: 23,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {i + 1}
          </span>
          <span style={{ fontSize: 27, fontWeight: 700, color: skin(dark).ink }}>{t}</span>
        </div>
      ))}
      <div style={{ fontSize: 25, fontWeight: 800, color: skin(dark).muted, marginTop: 14 }}>
        Free, instant, no sign-in needed
      </div>
    </Panel>
  </div>
);

// ---------------------------------------------------------------- about CTA
export const AboutCtaMock = ({ dark }) => (
  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 16, justifyContent: "center" }}>
    {[
      "What Foinn.org does, feature by feature",
      "Plans, prices and the add-on packs",
      "How to put it on your phone",
      "The FAQ, and where the tunes come from",
    ].map((t) => (
      <Panel key={t} dark={dark} style={{ padding: 20 }}>
        <span style={{ fontSize: 29, fontWeight: 800, color: skin(dark).ink }}>{t}</span>
      </Panel>
    ))}
    <div style={{ fontSize: 30, fontWeight: 900, color: PURPLE, marginTop: 6 }}>
      foinn.org/about — every answer in one page
    </div>
  </div>
);
